/* eslint-disable complexity */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { SummaryTechFeaturesService } from '../summary-tech-features-service';
import { NoveltyInputSummaryApi, type SummarizeNoveltyInputResponse } from '@/infrastructure/api-functions/core-drafting-api/novelty-input-summary-api';
import { RetrievalResultReportModel } from '../../models/retrieval-result-report-model';
import { sleep } from '@telecom/shared/utils/task';
import { CurrentTaskService } from '@/services/current-task/current-task-service';

/**
 * 轮询延迟时间（毫秒）
 */
const SUMMARY_POLLING_DELAY = 1000;

/**
 * 技术要点总结任务状态常量
 */
const SUMMARY_STATUS = {
    RUNNING: 'RUNNING' as const,
    SUCCESS: 'SUCCESS' as const,
    FAILED: 'FAILED' as const,
};

@injectable()
export class SummaryTechFeaturesServiceImpl implements SummaryTechFeaturesService {
    constructor(
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(NoveltyInputSummaryApi)
        private readonly _noveltyInputSummaryApi: NoveltyInputSummaryApi,
        @inject(RetrievalResultReportModel)
        private readonly _retrievalResultReportModel: RetrievalResultReportModel,
    ) { }

    /**
     * 技术要点总结轮询中断控制器
     */
    private _summaryAbortController?: AbortController;

    private _onSummaryLoadingEmitter = new Emitter<boolean>();

    get onSummaryLoading() {
        return this._onSummaryLoadingEmitter.event;
    }

    private get taskId() {
        return this._currentTaskService.taskId;
    }

    async pullSummary(taskId: string = this.taskId): Promise<string> {
        const text = await this._summaryRepository(taskId);
        if (text) {
            this._retrievalResultReportModel.update((state) => {
                state.techEssential = text;
                return state;
            });
        }
        return text;
    }

    private async _summaryRepository(taskId: string = this.taskId): Promise<SummarizeNoveltyInputResponse> {
        this._onSummaryLoadingEmitter.fire(true);

        // 创建新的中断控制器
        this._summaryAbortController = new AbortController();
        let isFinished = false;
        let result: SummarizeNoveltyInputResponse = '';

        try {
            const techEssential = this._retrievalResultReportModel.modelData.techEssential;

            while (!isFinished) {
                const response = await this._noveltyInputSummaryApi.summarizeNoveltyInput({
                    task_id: taskId,
                    text: techEssential,
                }, this._summaryAbortController.signal);

                if (response.status === SUMMARY_STATUS.SUCCESS) {
                    result = response.result || '';
                    isFinished = true;
                } else if (response.status === SUMMARY_STATUS.FAILED) {
                    throw new Error(response.error_message || '技术要点总结生成失败');
                } else if (response.status === SUMMARY_STATUS.RUNNING) {
                    // 继续轮询，等待1秒后重试
                    await sleep(SUMMARY_POLLING_DELAY);
                }
            }
        } finally {
            this._onSummaryLoadingEmitter.fire(false);
            this._summaryAbortController = undefined;
        }

        return result;
    }

    /**
     * 终止技术要点总结生成
     */
    terminateSummary(): void {
        if (this._summaryAbortController) {
            this._summaryAbortController.abort();
            this._summaryAbortController = undefined;
        }
    }
}
